import moment from "moment";
import { ALARM_CATEGORY_COLOR } from "../Common/Constants";

export const getUniqueBy = (arr, key) => {
  const keys = new Set();
  return arr.filter((el) => !keys.has(el[key]) && keys.add(el[key]));
};

export const sortData = (a, b) => {
  if (a.last_nom > b.last_nom) return 1;
  if (a.last_nom < b.last_nom) return -1;
  return 0;
};

export function getAlarmCategoryColor(type) {
  return ALARM_CATEGORY_COLOR[type] ?? "red";
}

export function getAlarmCategoryList() {
  return Object.keys(ALARM_CATEGORY_COLOR).map((key) => key);
}

export function downloadFile({ data, name }) {
  const href = URL.createObjectURL(data);

  // create "a" HTML element with href to file & click
  const link = document.createElement("a");
  link.href = href;
  link.setAttribute("download", name);
  document.body.appendChild(link);
  link.click();

  // clean up "a" element & remove ObjectURL
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

export function extractMasterRoleIds(roleHierarchiesObj) {
  let masterRoleIds = [];

  if (roleHierarchiesObj.masterRoleId) {
    masterRoleIds.push(roleHierarchiesObj.masterRoleId);
  }

  if (roleHierarchiesObj.roleGroup) {
    roleHierarchiesObj.roleGroup.forEach((role) => {
      masterRoleIds = [...masterRoleIds, ...extractMasterRoleIds(role)];
    });
  }

  return masterRoleIds;
}

export function extractAllowedUserRoleList(roleHierarchiesObj) {
  let roleList = [];

  if (roleHierarchiesObj.masterRoleId) {
    roleList.push({
      id: roleHierarchiesObj.masterRoleId,
      value: roleHierarchiesObj.roleName,
      gazzettedOrNotGazzetted: roleHierarchiesObj?.gazzettedOrNotGazzetted || "",
    });
  }

  if (roleHierarchiesObj?.roleGroup?.length > 0) {
    roleHierarchiesObj.roleGroup.forEach((role) => {
      roleList = [...roleList, ...extractAllowedUserRoleList(role)];
    });
  }

  return roleList;
}

export function extractUserDataFromPermission(jsonObj) {
  const outermostKeys = [];
  const outerToInnerKeys = {};
  const innerToCustomerNames = {};
  const innerToCustomers = {};
  const customerNameToId = {};
  const customerNameToIdObject = {};
  const _innerKeysList = new Set();
  const customerList = [];

  for (const obj of jsonObj) {
    for (const key in obj) {
      if (Array.isArray(obj[key])) {
        outermostKeys.push(key);
        const innerObjs = obj[key];
        const innerKeys = innerObjs.reduce((acc, innerObj) => {
          const keys = Object.keys(innerObj);
          for (const innerKey of keys) {
            if (innerKey !== "customerId" && innerKey !== "customerName") {
              if (!innerToCustomerNames[innerKey]) {
                innerToCustomerNames[innerKey] = [];
                innerToCustomers[innerKey] = [];
              }
              const childObjs = innerObj[innerKey];
              _innerKeysList.add(innerKey);
              for (const childObj of childObjs) {
                if (childObj && childObj.customerName) {
                  innerToCustomers[innerKey].push(childObj);
                  innerToCustomerNames[innerKey].push(childObj.customerName);
                  if (!customerNameToId[childObj.customerName]) {
                    customerNameToId[childObj.customerName] = [];
                  }
                  if (!customerNameToId[childObj.customerName]) {
                    customerNameToIdObject[childObj.customerName] = "";
                  }
                  customerNameToId[childObj.customerName].push(childObj.customerId);
                  customerNameToIdObject[childObj.customerName] = childObj.customerId;

                  childObj.id = childObj.customerId;
                  childObj.label = childObj.customerName;
                  customerList.push(childObj);
                }
              }
            }
          }
          return acc.concat(keys);
        }, []);
        outerToInnerKeys[key] = innerKeys;
      }
    }
  }
  const innerKeysList = [..._innerKeysList];
  innerKeysList.sort();
  return {
    outermostKeys,
    outerToInnerKeys,
    innerToCustomerNames,
    customerNameToId,
    innerKeysList,
    customerList,
    innerToCustomers,
    customerNameToIdObject,
  };
}

export const extractParsedDataFromPermissions = (permissions) => {
  const zones = [];
  const divisions = [];
  const stations = [];
  permissions.forEach((zone_object) => {
    Object.entries(zone_object).forEach(([zone_name, zone_child]) => {
      const _zone_obj = { id: zone_name, value: zone_name };
      zones.push(_zone_obj);

      zone_child.forEach((division_obj) => {
        Object.entries(division_obj).forEach(([division_name, division_child]) => {
          const _div_obj = {
            id: division_name,
            value: division_name,
            zone: _zone_obj,
          };
          divisions.push(_div_obj);

          division_child.forEach((station_obj) => {
            const _stn_obj = {
              id: station_obj.customerId,
              value: station_obj.customerName,
              zone: _zone_obj,
              division: { id: _div_obj.id, value: _div_obj.value },
            };
            stations.push(_stn_obj);
          });
        });
      });
    });
  });
  return { zones, divisions, stations };
};

export function formatDate(ts) {
  const value = Number(ts);
  const date = new Date(value);
  const yyyy = date.getFullYear();
  let mm = date.getMonth() + 1; // Months start at 0!
  let dd = date.getDate();

  if (dd < 10) dd = "0" + dd;
  if (mm < 10) mm = "0" + mm;

  const formattedToday = dd + "/" + mm + "/" + yyyy;
  const time = date.toLocaleTimeString();
  return formattedToday + " " + time;
}

export function isValidObject(arg) {
  return arg !== null && typeof arg === "object" && !Array.isArray(arg);
}

export function getMoreRecentDate(dateStr1, dateStr2) {
  const isValid = (d) => d != null && !isNaN(new Date(d).getTime());

  const valid1 = isValid(dateStr1);
  const valid2 = isValid(dateStr2);

  if (!valid1 && !valid2) return null;
  if (valid1 && !valid2) return dateStr1;
  if (!valid1 && valid2) return dateStr2;

  const date1 = new Date(dateStr1);
  const date2 = new Date(dateStr2);

  if (date1 > date2) return dateStr1;
  if (date2 > date1) return dateStr2;
  return dateStr1;
}
export function appendQueryParams(url, params) {
  let _url = `${url}`;

  Object.entries(params ?? {}).forEach(([queryKey, queryVal]) => {
    if (_url.includes("?")) {
      _url = _url + "&" + queryKey + "=" + queryVal;
    } else {
      console.log(`${_url}?${queryKey}=${queryVal}`);
      _url = _url + "?" + queryKey + "=" + queryVal;
    }
  });
  return _url;
}
